import { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import { SweetAlertResult } from 'sweetalert2';
import { ActionContext, Commit, Dispatch, Module } from 'vuex';
import api from '@/services/Api';
import Belegposition from '@/types/Belegposition';
import { confirmSwal, errorSwal, successSwal, toastSwal, warningSwal } from '@/helper/swals';
import Beleg from '@/types/Beleg';
import { Verrechnungsart } from '@/types/Verrechnungsart';
import Karteieintrag from '@/types/Karteieintrag';
import { ExtendedPatientState, PatientrechnungenState, RootState } from '@/store/types';
import Patient from '@/types/Patient';
import { DataResponse } from '@/types';
import BelegPatient from '@/types/BelegPatienten';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';
import { saveAs } from 'file-saver';

export type NeueRechnungPayload = {
    patienten: number[];
    erstordination: boolean;
    rechnungsempfaenger: number;
    hcw_einschliessen: string;
    sammelrechnung?: boolean;
    bis?: string;
};

export type UpdateRechnungPayload = {
    beleg: Beleg;
    belegpositionen: Belegposition[];
    count: number;
    sammelrechnung?: boolean;
    druckenUndAbschliessen?: boolean;
};

export type RechnungAllData = DataResponse<{
    belege: Partial<Belegposition>[];
    positionen: Partial<Belegposition>[];
    belegpatienten: Partial<BelegPatient>[];
}>;

type Context = ActionContext<PatientrechnungenState, RootState>;

async function rechnungAbschliessen(commit: Commit, dispatch: Dispatch, rootGetters: any, beleg_id: number | null): Promise<void> {
    commit('setLoadersBeleg', { abschluss: true });

    try {
        const response: AxiosResponse = await api.post('ivs/rechnung/complete', { beleg_id })

        if (response.data.body && typeof response.data.body !== 'string') {
            successSwal('Rechnung', 'abgeschlossen');
        } else {
            errorSwal('Fehler', 'Rechnung wurde bereits abgeschlossen oder abgebrochen.');
        }
    
        dispatch('warteliste/loadWarteliste', null, { root: true });
        
    } catch (error) {
        console.error(error);
    } finally {
            commit('setLoadersBeleg', { abschluss: false });
            commit('setDruckok', false);

            try {
                const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                        rootGetters['authentication/getSystemEinstellung'];

                commit('setAddAgainToWartelisteDialog', getSystemEinstellung('abfrage_wieder_auf_warteliste').isEnabled());
            } catch (error) {
                console.error(error);
            }

            commit('setDialogRechnung', false);
            commit('setDialogSammelrechnung', false);
            dispatch('loadImpfungen');
            dispatch('loadRechnungen');
            dispatch('authentication/loadImpfstelle', null, { root: true });
    }
}

export const patientrechnung: Module<PatientrechnungenState, RootState> = {
    namespaced: true,
    state: {
        druckvorgang: false,
        druckvorgangRuecksetzung: false,
        abbrechungsvorgang: false,
        abbrechungsvorgangAlle: false,
        loadRechnung: false,
        beleg: new Beleg(),
        belegPositionen: [],
        belegPatienten: [],
        rechnungen: [],
        rechnungenpositionen: [],
        rechnungenpatienten: [],
        selected_rechnung: new Beleg(),
    },
    actions: {
        loadRechnungen({ commit, rootState }: Context): void {
            const patnr = rootState.patient.patientData?.patnr;
            if (!patnr) {
                return;
            }

            api.post('ivs/rechnung/all', { patnr })
                .then((response: AxiosResponse) => {
                    if (patnr === rootState.patient.patientData?.patnr) {
                        commit('setRechnungen', response.data);
                    }
                })
                .catch(console.error);
        },
        neueRechnung({ commit, dispatch, rootState }: Context, payload: NeueRechnungPayload): void {
            commit('setLoadRechnung', true);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            if (data.sammelrechnung) {
                api.post('ivs/rechnung/createsammelrechnung', data)
                    .then((response: AxiosResponse) => {
                        if (response.data.body.inBearbeitung) {
                            confirmSwal(
                                'Aktive Rechnung',
                                'Eine Rechnung befindet sich momentan in Bearbeitung. Wollen Sie diese abbrechen?',
                                'Ja',
                                'Nein')
                                .then(({isConfirmed}: SweetAlertResult) => {
                                    if (isConfirmed) {
                                        dispatch('alleRechnungenAbbrechen', data);
                                    }
                                });
                        } else if (response.data.body.beleg) {
                            commit('setBeleg', response.data.body.beleg);
                            commit('setBelegPositionen', response.data.body.belegpositionen);
                            commit('setBelegPatienten', response.data.body.patienten);
                        } else {
                            warningSwal('Hinweis', 'Alle offenen Rechnungen wurden bereits abgeschlossen.');
                        }
                    })
                    .catch(console.error)
                    .finally(() => {
                        commit('setLoadRechnung', false);
                        dispatch('authentication/loadImpfstelle', null, {root: true});
                    });
            } else {
                api.post('ivs/rechnung/create', data)
                    .then((response: AxiosResponse) => {
                        if (response.data.body.inBearbeitung) {
                            confirmSwal(
                                'Aktive Rechnung',
                                'Eine Rechnung befindet sich momentan in Bearbeitung. Wollen Sie diese abbrechen?',
                                'Ja',
                                'Nein')
                                .then(({isConfirmed}: SweetAlertResult) => {
                                    if (isConfirmed) {
                                        dispatch('alleRechnungenAbbrechen', data);
                                    }
                                });
                        } else if (response.data.body.beleg) {
                            commit('setBeleg', response.data.body.beleg);
                            commit('setBelegPositionen', response.data.body.belegpositionen);
                            commit('setBelegPatienten', response.data.body.patienten);
                        } else {
                            warningSwal('Hinweis', 'Alle offenen Rechnungen wurden bereits abgeschlossen.');
                        }
                    })
                    .catch(console.error)
                    .finally(() => {
                        commit('setLoadRechnung', false);
                        dispatch('authentication/loadImpfstelle', null, {root: true});
                    });
            }
        },
        updateAbrechnung({ commit, dispatch, rootState, rootGetters }: Context, { count, ...payload }: UpdateRechnungPayload):
            void {
            commit('setDruckvorgang', true);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/rechnung/update', data)
                .then((response: AxiosResponse) => {
                    if (response.data.body && typeof response.data.body !== 'string') {
                        const beleg = new Beleg(response.data.body);

                        if (data.sammelrechnung) {
                            dispatch('rechnungpdf', { beleg, print: true, count, belegpositionen: data.belegpositionen });
                        } else {
                            dispatch('rechnungpdf', { beleg, print: true, count });
                        }

                        if (payload.druckenUndAbschliessen === true) {
                            rechnungAbschliessen(commit, dispatch, rootGetters, beleg.beleg_id);
                        } else {
                            commit('setDruckok', true);
                        }

                    } else {
                        errorSwal('Fehler', 'Rechnung wurde abgebrochen.');
                        commit('setDialogRechnung', false);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setDruckvorgang', false));
        },
        storno({ commit, dispatch, state, rootGetters }: Context, payload): void {
            commit('setLoadersBeleg', { storno: true });
            api.post('ivs/rechnung/cancel', { beleg_id: state.selected_rechnung.beleg_id, anmerkung: payload.anmerkung })
                .then(() => {
                    commit('setDruckansicht', false);
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');

                    const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                        rootGetters['authentication/getSystemEinstellung'];

                    if (getSystemEinstellung('cbird_vorhanden').isEnabled()) {
                        toastSwal('warning', 'Storno', 'Bitte Bon in Registrierkassenprogramm stornieren.', 5000);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBeleg', { storno: false }));
        },
        updateBeleg({ commit, dispatch }: Context, beleg: Beleg): Promise<boolean> {
            commit('setLoadersBeleg', { save: true });

            return api.post('ivs/rechnung/updatebeleg', beleg)
                .then((response: AxiosResponse) => {
                    commit('setLoadersBeleg', { save: false });
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');
                    commit('setSelectedRechnung', response.data.body);
                    return true;
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    commit('setLoadersBeleg', { save: false });
                    return false;
                });
        },
        updateZahlungscheinBeleg({ commit }: Context, beleg: Beleg): Promise<boolean> {
            commit('setLoadersBeleg', { save: true });

            return api.post('ivs/rechnung/updatezahlungscheinbeleg', beleg)
                .then((response: AxiosResponse) => {
                    commit('setLoadersBeleg', { save: false });
                    commit('setBeleg', response.data.body);
                    successSwal('Rechnung ' + response.data.body.jahr + ' | ' + response.data.body.belegnr, 'bezahlt');
                    return true;
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    commit('setLoadersBeleg', { save: false });
                    return false;
                });
        },
        rechnungAbbrechen({ commit, dispatch, state, rootState }: Context): void {
            commit('setAbbrechungsVorgang', true);

            const patnr = rootState.patient.patientData?.patnr;

            const data = {
                beleg_id: state.beleg.beleg_id,
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('ivs/rechnung/abort', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        const patienten = response.data.body?.patienten ?? [];
                        if (patnr && patienten.some((patient: number) => patient === patnr)) {
                            dispatch('loadImpfungen');
                            dispatch('loadRechnungen');
                        }
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Rechnungsabbruch Fehler.');
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setDialogRechnung', false);
                    commit('setDialogSammelrechnung', false);
                    commit('setAbbrechungsVorgang', false);
                    commit('setBelegPatienten', []);
                    commit('setBelegPositionen', []);
                    dispatch('authentication/loadImpfstelle', null, { root: true });
                });
        },
        alleRechnungenAbbrechen({ commit, dispatch, rootState }: Context,
                                payload: { impfstelle_id: number; patienten: number[] }): void {
            commit('setAbbrechungsVorgangAlle', true);

            const patnr = rootState.patient.patientData?.patnr;

            api.post('ivs/rechnung/abortall', payload)
                .then((response: AxiosResponse) => {
                    if (response.data.body) {
                        successSwal('Rechnung', 'abgebrochen');
                    } else {
                        errorSwal('Fehler', 'Es befinden sich keine Rechnungen in Bearbeitung.');
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setAbbrechungsVorgangAlle', false);

                    if (patnr && payload.patienten.some((patient) => patient === patnr)) {
                        dispatch('loadImpfungen');
                        dispatch('loadRechnungen');
                    }

                    dispatch('authentication/loadImpfstelle', null, { root: true });
                });
        },
        addAgainToWarteliste({ commit, dispatch, state, rootState }: Context): void {
            const patnr = rootState.patient.beleg?.patnr;
            if (patnr != null) {
                dispatch('patient/waitPatient', { patnr }, { root: true });
            }
            commit('setAddAgainToWartelisteDialog', false);
        },
        rechnungAbschliessen({ commit, dispatch, state, rootGetters }: Context): void {
            rechnungAbschliessen(commit, dispatch, rootGetters, state.beleg.beleg_id);
        },
        resetRechnungsdruck({ commit, dispatch, state, rootState }: Context): void {
            commit('setDruckvorgangRuecksetzung', true);

            const data = {
                beleg_id: state.beleg.beleg_id,
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('ivs/rechnung/reset', data)
                .then((response: AxiosResponse) => {
                    if (!response.data.body || typeof response.data.body === 'string') {
                        warningSwal('Warnung', response.data.body ?? 'Rechnung wurde bereits abgebrochen.');
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setDruckok', false);
                    commit('setDruckvorgangRuecksetzung', false);
                    dispatch('authentication/loadImpfstelle', null, { root: true });
                });
        },
        rechnungpdf({ commit, dispatch }: Context, payload: { beleg: Beleg; print: boolean; copy?: boolean; belegpositionen?: Belegposition[] }): void {
            const { print, beleg: { beleg_id, jahr, belegnr, name }, copy, belegpositionen } = payload;

            commit('setLoadersBeleg', { [print ? 'print' : 'export']: true });

            api.post('ivs/belege', { beleg_id, export: !print, copy, belegpositionen }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    if (print) {
                        dispatch('globals/printDokument', { data, ...payload }, { root: true });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(data, `Rechnung_${jahr}${belegnr}_${name}_${date}.pdf`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBeleg', { [print ? 'print' : 'export']: false }));
        },
        rechnungsxml({ commit }: Context, payload: { beleg: Beleg }): void {
            const { beleg: { beleg_id, jahr, belegnr, name } } = payload;

            commit('setLoadersBeleg', { exportXml: true });

            api.post('ivs/belege/xml', { beleg_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    saveAs(response.data, `Rechnung_${jahr}_${belegnr}_${name?.replace(/ /g, '_') ?? ''}_${date}.xml`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBeleg', { exportXml: false }));
        },
        rechnungBerichtigung({ commit, dispatch, state }: Context): void {
            commit('setLoadersBeleg', { berichtigung: true });

            api.post('ivs/rechnung/berichtigung', { beleg_id: state.selected_rechnung.beleg_id })
                .then(() => {
                    commit('setDruckansicht', false);
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBeleg', { berichtigung: false }));
        },
        rechnungMahnung({ commit, dispatch, state }: Context): void {
            commit('setLoadersBeleg', { mahnung: true });

            api.post('ivs/rechnung/mahnung', { beleg_id: state.selected_rechnung.beleg_id })
                .then(() => {
                    commit('setDruckansicht', false);
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBeleg', { mahnung: false }));
        },
    },
    mutations: {
        setDialogRechnung(state: PatientrechnungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.rechnung = visible;

            if (visible) {
                state.beleg = new Beleg();
                state.belegPositionen = [];
                state.belegPatienten = [];
            } else {
                (state as ExtendedPatientState).zahlendePatienten = [];
            }
        },
        setBeleg(state: PatientrechnungenState, payload: Partial<Beleg>): void {
            state.beleg = new Beleg(payload);
        },
        setBelegPositionen(state: PatientrechnungenState, payload: Partial<Belegposition>[]): void {
            state.belegPositionen = payload.map((data) => new Belegposition(data));
        },
        setBelegPatienten(state: PatientrechnungenState, payload: Partial<Patient>[]): void {
            state.belegPatienten = payload.map((data) => new Patient(data));
        },
        setDruckvorgang(state: PatientrechnungenState, loading: boolean): void {
            state.druckvorgang = loading;
        },
        setDruckvorgangRuecksetzung(state: PatientrechnungenState, loading: boolean): void {
            state.druckvorgangRuecksetzung = loading;
        },
        setAbbrechungsVorgang(state: PatientrechnungenState, loading: boolean): void {
            state.abbrechungsvorgang = loading;
        },
        setAbbrechungsVorgangAlle(state: PatientrechnungenState, loading: boolean): void {
            state.abbrechungsvorgangAlle = loading;
        },
        setDruckok(state: PatientrechnungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.druckok = visible;
        },
        setAddAgainToWartelisteDialog(state: PatientrechnungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.addAgainToWartelisteDialog = visible;
        },
        setLoadRechnung(state: PatientrechnungenState, loading: boolean): void {
            state.loadRechnung = loading;
        },
        setRechnungen(state: PatientrechnungenState, data: RechnungAllData): void {
            state.rechnungen = data.body.belege.map((data) => new Beleg(data));
            state.rechnungenpositionen = data.body.positionen.map((data) => new Belegposition(data));
            state.rechnungenpatienten = data.body.belegpatienten.map((data) => new BelegPatient(data));
        },
        setSelectedRechnung(state: PatientrechnungenState, payload: Beleg): void {
            state.selected_rechnung = new Beleg(payload);
        },
        setDruckansicht(state: PatientrechnungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.druckansicht = visible;
        },
        setLoadersDruck(state: PatientrechnungenState, visible: boolean): void {
            (state as ExtendedPatientState).loaders.druck = visible;
        },
    },
    getters: {
        getNichtbezahlteImpfungen: (state: PatientrechnungenState): Karteieintrag[] => {
            return (state as ExtendedPatientState).impfungen.filter((i) => i.abrechnung === Verrechnungsart.Offen);
        },
        getSelectedRechnung: (state: PatientrechnungenState): { rechnung: Beleg, positionen: Belegposition[] } => {
            return {
                rechnung: state.selected_rechnung,
                positionen: state.rechnungenpositionen.filter((position) => {
                    return position.beleg_id === state.selected_rechnung.beleg_id;
                }),
            };
        },
        getNrRechnungen: (state: PatientrechnungenState): number => {
            return state.rechnungen.length;
        },
    },
};
